import React, { useState, useEffect, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Brain, Target, MessageCircle, BookOpen, ArrowRight, Settings, Info } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Variantes de animación para las tarjetas
const cardVariants = {
  initial: { opacity: 0, y: 20 },
  animate: (index) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: index * 0.1,
    },
  }),
  hover: {
    y: -5,
    transition: {
      duration: 0.2,
    },
  },
  tap: {
    y: 0,
    scale: 0.98,
    transition: {
      duration: 0.1,
    },
  },
  exit: {
    opacity: 0,
    y: 10,
    transition: {
      duration: 0.2,
    },
  }
};

// Variantes de animación para los iconos
const iconVariants = {
  initial: { scale: 0.8, rotate: -10 },
  animate: { scale: 1, rotate: 0 },
  hover: {
    scale: 1.1,
    rotate: 5,
    transition: { type: "spring", stiffness: 300 }
  },
  tap: {
    scale: 0.95,
    rotate: 0,
    transition: { type: "spring", stiffness: 500 }
  }
};

// Componente para mostrar tooltips
const Tooltip = ({ children, content, isVisible, position = "top" }) => {
  const positionClasses = {
    top: "bottom-full mb-2",
    bottom: "top-full mt-2",
    left: "right-full mr-2",
    right: "left-full ml-2"
  };

  return (
    <div className="relative group">
      {children}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className={`absolute ${positionClasses[position]} z-10 px-2 py-1 text-xs font-medium text-white bg-gray-800 rounded-md whitespace-nowrap`}
          >
            {content}
            <div className={`absolute w-2 h-2 bg-gray-800 transform rotate-45 ${position === "top" ? "top-full -mt-1" : "bottom-full -mb-1"} left-1/2 -translate-x-1/2`}></div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Componente principal de QuickActions mejorado
const QuickActions = ({ maxVisible = 4, customTheme = null }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [userPreferences, setUserPreferences] = useState(() => {
    // Cargar preferencias del localStorage o usar predeterminadas
    const savedPrefs = localStorage.getItem('quickActionsPrefs');
    return savedPrefs ? JSON.parse(savedPrefs) : { order: [], hidden: [] };
  });

  // Simular carga de datos
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  // Obtener datos contextuales (simulado)
  const getContextData = useCallback(() => {
    return {
      journal: 0,
      goals: 0,
      challenges: 0,
      ai: 0
    };
  }, []);

  const contextData = getContextData();

  // Definición de acciones rápidas
  const getActions = useCallback((t) => [
    // {
    //   id: 'challenge',
    //   icon: Brain,
    //   label: t('quickActions.dailyChallenge'),
    //   description: t('quickActions.dailyChallengeDesc', 'Complete daily exercises to improve your skills'),
    //   path: '/app/challenges',
    //   color: 'bg-gradient-to-br from-purple-500 to-purple-600 dark:from-purple-600 dark:to-purple-700',
    //   borderColor: 'border-purple-200 dark:border-purple-900/20',
    //   shadowColor: 'shadow-purple-500/10',
    //   contextCount: contextData.challenges,
    //   shortcut: 'Alt+1'
    // },
    {
      id: 'goal',
      icon: Target,
      label: t('quickActions.newGoal'),
      description: t('quickActions.newGoalDesc', 'Create a new goal to track your progress'),
      path: '/app/goals',
      color: 'bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700',
      borderColor: 'border-blue-200 dark:border-blue-900/20',
      shadowColor: 'shadow-blue-500/10',
      contextCount: contextData.goals,
      shortcut: 'Alt+2',
      onClick: () => {
        setLoading(true);
        setTimeout(() => {
          navigate('/app/goals', { state: { openNewGoal: true } });
          setLoading(false);
        }, 100);
      }
    },
    // {
    //   id: 'ai',
    //   icon: MessageCircle,
    //   label: t('quickActions.aiConsult'),
    //   description: t('quickActions.aiConsultDesc', 'Get personalized advice from AI'),
    //   path: '/app/ai-mentor',
    //   color: 'bg-gradient-to-br from-green-500 to-green-600 dark:from-green-600 dark:to-green-700',
    //   borderColor: 'border-green-200 dark:border-green-900/20',
    //   shadowColor: 'shadow-green-500/10',
    //   contextCount: contextData.ai,
    //   shortcut: 'Alt+3'
    // },
    {
      id: 'journal',
      icon: BookOpen,
      label: t('quickActions.viewJournal'),
      description: t('quickActions.viewJournalDesc', 'Review and add to your journal entries'),
      path: '/app/journal',
      color: 'bg-gradient-to-br from-orange-500 to-orange-600 dark:from-orange-600 dark:to-orange-700',
      borderColor: 'border-orange-200 dark:border-orange-900/20',
      shadowColor: 'shadow-orange-500/10',
      contextCount: contextData.journal,
      shortcut: 'Alt+4',
      current: location.pathname === '/app/journal'
    },
  ], [t, navigate, contextData, location.pathname]);

  // Función para ordenar acciones según preferencias del usuario
  const getSortedActions = useCallback(() => {
    let actions = getActions(t);

    // Filtrar acciones ocultas
    if (userPreferences.hidden && userPreferences.hidden.length > 0) {
      actions = actions.filter(action => !userPreferences.hidden.includes(action.id));
    }

    // Ordenar según preferencias
    if (userPreferences.order && userPreferences.order.length > 0) {
      const orderedActions = [];

      // Primero agregar acciones en el orden especificado
      userPreferences.order.forEach(id => {
        const action = actions.find(a => a.id === id);
        if (action) orderedActions.push(action);
      });

      // Agregar acciones restantes que no estén en el orden especificado
      actions.forEach(action => {
        if (!userPreferences.order.includes(action.id)) {
          orderedActions.push(action);
        }
      });

      actions = orderedActions;
    }

    // Limitar cantidad visible
    return actions.slice(0, maxVisible);
  }, [getActions, t, userPreferences, maxVisible]);

  // Manejar atajos de teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key >= '1' && e.key <= '4') {
        const index = parseInt(e.key) - 1;
        const actions = getSortedActions();

        if (actions[index]) {
          e.preventDefault();
          if (actions[index].onClick) {
            actions[index].onClick();
          } else {
            navigate(actions[index].path);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [getSortedActions, navigate]);

  // Renderizar tarjetas de acciones
  const renderActionCard = (action, index) => {
    const {
      id, icon: Icon, label, description, path, color, borderColor,
      shadowColor, onClick, contextCount, shortcut, current
    } = action;

    const Component = onClick ? 'button' : Link;
    const props = onClick ? {
      onClick,
      type: "button"
    } : {
      to: path
    };

    // Determinar si el componente debe mostrar estado de carga
    const isLoading = loading && (
      (onClick && typeof onClick === 'function') ||
      (current && location.pathname === path)
    );

    // Clases para estado activo/actual
    const currentStateClasses = current
      ? 'ring-2 ring-offset-2 ring-blue-500 dark:ring-blue-400'
      : '';

    return (
      <motion.div
        key={id}
        variants={cardVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        whileHover="hover"
        whileTap="tap"
        custom={index}
        className={`${shadowColor} shadow-lg`}
        onMouseEnter={() => setActiveTooltip(id)}
        onMouseLeave={() => setActiveTooltip(null)}
        onFocus={() => setActiveTooltip(id)}
        onBlur={() => setActiveTooltip(null)}
      >
        <Component
          {...props}
          aria-label={label}
          aria-describedby={`tooltip-${id}`}
          aria-current={current ? 'page' : undefined}
          className={`relative w-full h-full bg-white dark:bg-gray-800 p-4 rounded-xl border ${borderColor} 
            backdrop-blur-sm flex flex-col items-start space-y-3 
            transition-all group overflow-hidden
            focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500
            active:scale-98 touch-action-manipulation ${currentStateClasses}`}
          disabled={isLoading}
        >
          <motion.div
            variants={iconVariants}
            className={`${color} p-2.5 rounded-lg shadow-lg`}
          >
            {isLoading ? (
              <div className="w-6 h-6 rounded-full border-2 border-white border-t-transparent animate-spin"></div>
            ) : (
              <Icon className="w-6 h-6 text-white" />
            )}
          </motion.div>

          <div className="flex items-center justify-between w-full">
            <div className="flex flex-col">
              <span className="font-medium text-sm text-gray-700 dark:text-gray-200 line-clamp-2">
                {label}
                {contextCount > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-xs">
                    {contextCount}
                  </span>
                )}
              </span>

              {/* {shortcut && (
                <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 hidden sm:inline-block">
                  {shortcut}
                </span>
              )} */}
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400 dark:text-gray-500 
              transition-transform group-hover:translate-x-1" />
          </div>

          {/* Decorative gradient blur */}
          <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity
            pointer-events-none bg-gradient-to-br ${color} blur-3xl scale-150 -z-10`} />

          {/* Tooltip */}
          <div
            id={`tooltip-${id}`}
            role="tooltip"
            className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded
              opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10
              ${activeTooltip === id ? 'opacity-100' : 'opacity-0'}`}
          >
            {description}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-2 h-2 bg-gray-900 rotate-45"></div>
          </div>
        </Component>
      </motion.div>
    );
  };

  // Componente de esqueleto para el estado de carga
  const SkeletonCard = ({ index }) => (
    <motion.div
      variants={cardVariants}
      initial="initial"
      animate="animate"
      custom={index}
      className="shadow-lg shadow-gray-200/20 dark:shadow-gray-900/10"
    >
      <div className="relative w-full bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700/20 
        backdrop-blur-sm flex flex-col items-start space-y-3 transition-all overflow-hidden">
        <div className="bg-gray-200 dark:bg-gray-700 p-2.5 rounded-lg w-11 h-11 animate-pulse"></div>
        <div className="flex items-center justify-between w-full">
          <div className="bg-gray-200 dark:bg-gray-700 h-5 w-24 rounded animate-pulse"></div>
          <div className="bg-gray-200 dark:bg-gray-700 h-4 w-4 rounded animate-pulse"></div>
        </div>
      </div>
    </motion.div>
  );

  const actions = getSortedActions();

  return (
    <div className="relative">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {loading ? (
          // Mostrar esqueletos durante la carga
          Array.from({ length: maxVisible }).map((_, index) => (
            <SkeletonCard key={`skeleton-${index}`} index={index} />
          ))
        ) : (
          // Mostrar tarjetas de acciones
          <AnimatePresence>
            {actions.map((action, index) => renderActionCard(action, index))}
          </AnimatePresence>
        )}
      </div>

      {/* Botón de configuración */}
      {/* <button
        className="absolute -top-10 right-0 p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 
          rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        aria-label={t('quickActions.settings', 'Configure quick actions')}
        onClick={() => alert(t('quickActions.settingsMessage', 'Settings functionality would be implemented here'))}
      >
        <Settings className="w-5 h-5" />
      </button> */}

      {/* Info de accesibilidad */}
      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 hidden md:block">
        <div className="flex items-center">
          <Info className="w-3 h-3 mr-1" />
          <span>{t('quickActions.shortcuts', 'Pro tip: Use Alt+1 through Alt+4 for quick access')}</span>
        </div>
      </div>
    </div>
  );
};

// Exportar componente con memoización para mejorar rendimiento
export default memo(QuickActions);